import { getDatabase, ref, get, set, update, push } from 'firebase/database';
import { GroupTag } from '../types/protocols';
import { recordActivity } from './activityApi';

const BASE_PATH = 'buildingTags';

/**
 * Fetch all building group tags, sorted alphabetically by tag name.
 */
export async function getBuildingTags(): Promise<GroupTag[]> {
    try {
        const db = getDatabase();
        const snapshot = await get(ref(db, BASE_PATH));

        if (!snapshot.exists()) return [];

        const data = snapshot.val();
        const tags: GroupTag[] = [];
        Object.entries(data).forEach(([key, value]: [string, any]) => {
            if (value && typeof value === 'object' && value.tag) {
                tags.push({ key, tag: value.tag });
            }
        });
        tags.sort((a, b) => a.tag.localeCompare(b.tag));
        return tags;
    } catch (err) {
        console.error('Failed to load group tags data', err);
        return [];
    }
}

/**
 * Create a new building group tag. Returns the created tag.
 */
export async function addBuildingTag(tag: string): Promise<GroupTag> {
    const name = tag.trim();
    if (!name) {
        throw new Error('Namn krävs');
    }

    const db = getDatabase();
    const newRef = push(ref(db, BASE_PATH));
    await set(newRef, { tag: name });
    recordActivity('settings.updated', { targetKey: newRef.key ?? undefined, label: name });
    return { key: newRef.key!, tag: name };
}

/**
 * Rename an existing building group tag.
 */
export async function updateBuildingTag(key: string, tag: string): Promise<void> {
    const name = tag.trim();
    if (!name) {
        throw new Error('Namn krävs');
    }

    const db = getDatabase();
    await update(ref(db, `${BASE_PATH}/${key}`), { tag: name });
    recordActivity('settings.updated', { targetKey: key, label: name });
}

/**
 * Delete a building group tag definition and remove it from all associated buildings.
 *
 * Performs a single atomic multi-location update: nulls the global definition under
 * `buildingTags/{key}` and every assignment at
 * `buildingsSummary/{buildingKey}/groupTags/{key}` for the given building keys.
 */
export async function deleteBuildingTag(key: string, buildingKeys: string[] = []): Promise<void> {
    const db = getDatabase();
    const updates: Record<string, null> = {
        [`${BASE_PATH}/${key}`]: null,
    };
    buildingKeys.forEach((buildingKey) => {
        updates[`buildingsSummary/${buildingKey}/groupTags/${key}`] = null;
    });
    await update(ref(db), updates);
    recordActivity('settings.updated', { targetKey: key });
}
